/**
 * Contexto de Toast do Sistema MEDICARE
 * Gerencia notificações e mensagens para o usuário
 */

import React, { createContext, useContext, ReactNode } from 'react';
import { useToast, UseToastOptions } from '@chakra-ui/react';

// Tipos personalizados para toast
interface ToastContextType {
  showToast: (options: ToastOptions) => void;
  showSuccess: (message: string, title?: string) => void;
  showError: (message: string, title?: string) => void;
  showWarning: (message: string, title?: string) => void;
  showInfo: (message: string, title?: string) => void;
  showLoading: (message: string, title?: string) => string;
  closeToast: (id: string) => void;
  closeAll: () => void;
}

interface ToastOptions extends Omit<UseToastOptions, 'status'> {
  status?: 'success' | 'error' | 'warning' | 'info' | 'loading';
}

// Criação do contexto
const ToastContext = createContext<ToastContextType | undefined>(undefined);

// Provider do contexto
interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const toast = useToast();

  // Configurações padrão para diferentes tipos de toast
  const defaultOptions: Record<string, Partial<UseToastOptions>> = {
    success: {
      status: 'success',
      duration: 4000,
      isClosable: true,
      position: 'top-right',
    },
    error: {
      status: 'error',
      duration: 6000,
      isClosable: true,
      position: 'top-right',
    },
    warning: {
      status: 'warning',
      duration: 5000,
      isClosable: true,
      position: 'top-right',
    },
    info: {
      status: 'info',
      duration: 4000,
      isClosable: true,
      position: 'top-right',
    },
    loading: {
      status: 'loading',
      duration: null,
      isClosable: false,
      position: 'top-right',
    },
  };

  // Função genérica para mostrar toast
  const showToast = (options: ToastOptions): void => {
    const { status = 'info', ...restOptions } = options;
    const defaults = defaultOptions[status] || defaultOptions.info;
    
    toast({
      ...defaults,
      ...restOptions,
      status: status as any,
    });
  };

  // Função para toast de sucesso
  const showSuccess = (message: string, title?: string): void => {
    showToast({
      title: title || 'Sucesso',
      description: message,
      status: 'success',
    });
  };

  // Função para toast de erro
  const showError = (message: string, title?: string): void => {
    showToast({
      title: title || 'Erro',
      description: message,
      status: 'error',
    });
  };

  // Função para toast de aviso
  const showWarning = (message: string, title?: string): void => {
    showToast({
      title: title || 'Atenção',
      description: message,
      status: 'warning',
    });
  };

  // Função para toast de informação
  const showInfo = (message: string, title?: string): void => {
    showToast({
      title: title || 'Informação',
      description: message,
      status: 'info',
    });
  };

  // Função para toast de loading
  const showLoading = (message: string, title?: string): string => {
    const id = toast({
      title: title || 'Carregando...',
      description: message,
      status: 'loading',
      duration: null,
      isClosable: false,
      position: 'top-right',
    });
    
    return id as string;
  };

  // Função para fechar toast específico
  const closeToast = (id: string): void => {
    toast.close(id);
  };

  // Função para fechar todos os toasts
  const closeAll = (): void => {
    toast.closeAll();
  };

  // Valor do contexto
  const contextValue: ToastContextType = {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showLoading,
    closeToast,
    closeAll,
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
    </ToastContext.Provider>
  );
};

// Hook para usar o contexto
export const useToastContext = (): ToastContextType => {
  const context = useContext(ToastContext);
  
  if (context === undefined) {
    throw new Error('useToastContext deve ser usado dentro de um ToastProvider');
  }
  
  return context;
};

// Hook personalizado para operações comuns
export const useNotifications = () => {
  const { showSuccess, showError, showWarning, showInfo, showLoading, closeToast } = useToastContext();

  return {
    // Notificações de operações CRUD
    notifyCreate: (entity: string) => showSuccess(`${entity} criado(a) com sucesso!`),
    notifyUpdate: (entity: string) => showSuccess(`${entity} atualizado(a) com sucesso!`),
    notifyDelete: (entity: string) => showSuccess(`${entity} removido(a) com sucesso!`),
    
    // Notificações de autenticação
    notifyLogin: () => showSuccess('Login realizado com sucesso!', 'Bem-vindo(a)'),
    notifyLogout: () => showInfo('Você foi desconectado do sistema.', 'Até logo'),
    notifySessionExpired: () => showWarning('Sua sessão expirou. Faça login novamente.', 'Sessão Expirada'),
    
    // Notificações de consultas
    notifyConsultaAgendada: () => showSuccess('Consulta agendada com sucesso!'),
    notifyConsultaCancelada: () => showInfo('Consulta cancelada.'),
    notifyConsultaConcluida: () => showSuccess('Consulta concluída!'),
    
    // Notificações de erro comuns
    notifyNetworkError: () => showError('Erro de conexão. Verifique sua internet.', 'Erro de Rede'),
    notifyServerError: () => showError('Erro interno do servidor. Tente novamente.', 'Erro do Servidor'),
    notifyValidationError: (message: string) => showError(message, 'Dados Inválidos'),
    notifyPermissionError: () => showError('Você não tem permissão para esta ação.', 'Acesso Negado'),
    
    // Notificações de loading
    notifyLoading: (message: string) => showLoading(message),
    notifyLoadingComplete: (id: string) => closeToast(id),
    
    // Notificações personalizadas
    success: showSuccess,
    error: showError,
    warning: showWarning,
    info: showInfo,
    // Aliases para compatibilidade
    showSuccess,
    showError,
  };
};

export default ToastContext;